import get from 'lodash.get';
// --- DEBUG import (temporary; remove with the debug logging below) ---
import Cookies from 'js-cookie';

import apiClient from '../middleware/apiClient';
import {
  SWAP_ELIGIBILITY, COTTON_SWAP_ELIGIBILITY, SWAPS_ORDER, COTTON_SWAPS_ORDER,
} from '../helpers/apis';
import { getAccount } from './account';
import { getDeviceFromAccount } from '../helpers/account';
import { getEligibleDevices } from '../helpers/swaps';
import { callApiWithFallback } from '../helpers/apiFallback';
import cottonFlagManager from '../helpers/featureFlags/cotton';
// --- DEBUG imports (temporary; remove with the debug logging below) ---
import { kibanaLog, getGuid } from '../helpers/logger';
import { lowerEnvConsoleLog, lowerEnvConsoleWarn } from '../helpers/lowerEnvConsoleLog';
import { isReady } from '../helpers/featureFlags';
import { getUserSegment } from '../helpers/segmentation';

// Fetch Swap Eligibility
export const GET_SWAP_ELIGIBILITY = 'GET_SWAP_ELIGIBILITY';
export const GET_SWAP_ELIGIBILITY_SUCCESS = 'GET_SWAP_ELIGIBILITY_SUCCESS';
export const GET_SWAP_ELIGIBILITY_FAILURE = 'GET_SWAP_ELIGIBILITY_FAILURE';
export const SET_NOT_SWAP_ELIGIBILITY = 'SET_NOT_SWAP_ELIGIBILITY';

function dispatchGetSwapEligibility(isKotlinEnabled) {
  return {
    type: GET_SWAP_ELIGIBILITY,
    payload: apiClient.fetch(isKotlinEnabled ? COTTON_SWAP_ELIGIBILITY : SWAP_ELIGIBILITY, {
      headers: {
        Accept: 'application/json; v=3',
        'api-version': '3', // SHAME: workaround until codebig is fixed
      },
    }),
  };
}

function dispatchGetSwapEligibilitySuccess(payload) {
  return {
    type: GET_SWAP_ELIGIBILITY_SUCCESS,
    payload,
  };
}

function dispatchGetSwapEligibilityFailure(error) {
  return {
    type: GET_SWAP_ELIGIBILITY_FAILURE,
    payload: error,
  };
}

function dispatchSetNotSwapEligibility() {
  return {
    type: SET_NOT_SWAP_ELIGIBILITY,
  };
}

export const getSwapEligibility = () => async (dispatch, getState) => {
  // TODO update to cache
  const { cached, loading } = getState().swapEligibility || {};
  if (cached) {
    return null;
  }

  try {
    let response;
    const { swapsEligibilityEnabled } = cottonFlagManager;

    // --- DEBUG: prove flag-evaluation/timing state at the swap-eligibility decision point ---
    const debugSnapshot = {
      api: 'swap_eligibility',
      guid: String(getGuid()),
      // in-flight state
      in_flight_loading: String(loading),
      in_flight_cached: String(cached),
      // flag-readiness (timing) proof
      flags_ready: String(isReady()),
      decision_before_flags_ready: String(!isReady()),
      // evaluated gating chain
      master_flag: String(cottonFlagManager.masterFlag),
      cotton_allowed: String(cottonFlagManager.cottonAllowed),
      is_base_enabled: String(cottonFlagManager.isBaseEnabled),
      swaps_eligibility_enabled: String(swapsEligibilityEnabled),
      disable_ruby_apis: String(cottonFlagManager.disableRubyApis),
      // cookies / segmentation (rule-out fields)
      cotton_allowed_cookie: String(Cookies.get('cotton_allowed')),
      xf_seg_cookie: String(Cookies.get('xf_seg')),
      user_segment: String(getUserSegment()),
    };
    lowerEnvConsoleLog('[DEBUG swap_eligibility gating]', debugSnapshot);
    kibanaLog('debug_swap_eligibility_gating', debugSnapshot);
    // --- END DEBUG ---

    if (swapsEligibilityEnabled) {
      // Try Cotton API, fallback to Ruby API
      response = await callApiWithFallback(
        dispatch,
        () => dispatchGetSwapEligibility(true), // Cotton API call
        () => dispatchGetSwapEligibility(false), // Ruby API call
        'swap_eligibility',
      );
    } else {
      // Only call Ruby API
      // --- DEBUG: direct Ruby branch => cotton flag evaluated false at request time ---
      lowerEnvConsoleWarn('[DEBUG swap_eligibility] RUBY-ONLY branch taken (cotton flag false)', debugSnapshot);
      kibanaLog('debug_swap_eligibility_ruby_only_branch', debugSnapshot);
      // --- END DEBUG ---
      response = await dispatch(dispatchGetSwapEligibility(false)).payload;
    }
    await dispatch(getAccount());

    const { account: { data: account } } = getState();
    const devices = getDeviceFromAccount(account);
    const eligibleDevices = getEligibleDevices(response, devices);
    if (eligibleDevices.length === 0) {
      return dispatch(dispatchSetNotSwapEligibility());
    }

    return dispatch(dispatchGetSwapEligibilitySuccess(response));
  } catch (error) {
    if (get(error, 'data.code') === 'SH404.127.2') {
      return dispatch(dispatchSetNotSwapEligibility());
    }

    return dispatch(dispatchGetSwapEligibilityFailure(error));
  }
};

// Fetch Swap Eligibility
export const POST_SWAP_ORDER = 'POST_SWAP_ORDER';
export const POST_SWAP_ORDER_SUCCESS = 'POST_SWAP_ORDER_SUCCESS';
export const POST_SWAP_ORDER_FAILURE = 'POST_SWAP_ORDER_FAILURE';

function dispatchPostSwapOrder(data, isKotlinEnabled) {
  // return {
  //   type: POST_SWAP_ORDER,
  //   payload: new Promise(resolve => setTimeout(() => resolve(), 2000)),
  // };

  return {
    type: POST_SWAP_ORDER,
    payload: apiClient.fetch(isKotlinEnabled ? COTTON_SWAPS_ORDER : SWAPS_ORDER, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  };
}

function dispatchPostSwapOrderSuccess(payload) {
  return {
    type: POST_SWAP_ORDER_SUCCESS,
    payload,
  };
}

function dispatchPostSwapOrderFailure(error) {
  return {
    type: POST_SWAP_ORDER_FAILURE,
    payload: error,
  };
}

export const postSwapOrder = data => async (dispatch) => {
  try {
    let response;
    if (cottonFlagManager.swapsOrderEnabled) {
      // Try Cotton API, fallback to Ruby API
      response = await callApiWithFallback(
        dispatch,
        () => dispatchPostSwapOrder(data, true), // Cotton API call
        () => dispatchPostSwapOrder(data, false), // Ruby API call
        'swap_order',
      );
    } else {
      // Only call Ruby API
      response = await dispatch(dispatchPostSwapOrder(data, false)).payload;
    }
    return dispatch(dispatchPostSwapOrderSuccess(response));
  } catch (error) {
    dispatch(dispatchPostSwapOrderFailure(error));
    throw error;
  }
};
